import React, { useState } from 'react';
import ExportButtons from './ExportButtons';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement, PointElement, LinearScale, CategoryScale,
  Title, Tooltip, Legend, ArcElement, BarElement, Filler
} from 'chart.js';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ArcElement, BarElement, Filler);

const tooltipStyle = {
  backgroundColor: 'rgba(18,18,30,0.95)',
  borderColor: 'rgba(255,255,255,0.1)',
  borderWidth: 1,
  padding: 12,
  cornerRadius: 8,
  titleColor: '#fff',
  bodyColor: 'rgba(255,255,255,0.8)',
};

const gridStyle = { color: 'rgba(255,255,255,0.04)' };
const tickStyle = { color: 'rgba(255,255,255,0.4)', font: { size: 11 } };
const noGrid = { display: false };
const noBorder = { display: false };

const CodebaseDevOps = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'fas fa-chart-pie' },
    { id: 'testing', label: 'Testing', icon: 'fas fa-vial' },
    { id: 'deployments', label: 'Deployments', icon: 'fas fa-rocket' },
    { id: 'bugs', label: 'Bugs', icon: 'fas fa-bug' },
  ];

  return (
    <div className="cd-container">
      <div className="cd-header">
        <div className="cd-header-left">
          <h4>Codebase & DevOps</h4>
          <p className="cd-subtitle">Test coverage, CI/CD pipelines, bug tracking & SDK inventory</p>
        </div>
        <ExportButtons
          data={[
            { module: 'Auth Service', coverage: '92%', tests: '247', passing: '243', status: 'Healthy' },
            { module: 'Payment Service', coverage: '88%', tests: '189', passing: '185', status: 'Healthy' },
            { module: 'Video Pipeline', coverage: '76%', tests: '312', passing: '298', status: 'Warning' },
            { module: 'User Service', coverage: '85%', tests: '156', passing: '153', status: 'Healthy' },
            { module: 'Content API', coverage: '71%', tests: '203', passing: '191', status: 'Warning' },
            { module: 'Analytics', coverage: '64%', tests: '98', passing: '89', status: 'Low' },
          ]}
          headers={[
            { key: 'module', label: 'Module' }, { key: 'coverage', label: 'Coverage' },
            { key: 'tests', label: 'Total Tests' }, { key: 'passing', label: 'Passing' }, { key: 'status', label: 'Status' },
          ]}
          title="Codebase & DevOps Report"
          filename="codebase_devops"
        />
      </div>

      <div className="cd-tabs">
        {tabs.map(t => (
          <button key={t.id} className={`cd-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
            <i className={t.icon}></i> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'testing' && <TestingTab />}
      {activeTab === 'deployments' && <DeploymentsTab />}
      {activeTab === 'bugs' && <BugsTab />}
    </div>
  );
};

const OverviewTab = () => {
  const kpiMetrics = [
    { label: 'Test Coverage', value: '78.4%', change: '+2.1%', positive: true, icon: 'fas fa-check-circle', color: '#10b981' },
    { label: 'Open Bugs', value: '142', change: '-8', positive: true, icon: 'fas fa-bug', color: '#ef4444' },
    { label: 'Deploy Frequency', value: '4.2/week', change: '+0.3', positive: true, icon: 'fas fa-rocket', color: '#6366f1' },
    { label: 'Avg Bug Fix Time', value: '2.3 days', change: '-0.4d', positive: true, icon: 'fas fa-hourglass-half', color: '#f59e0b' },
  ];

  const testCoverageByType = {
    labels: ['Backend Unit Tests', 'Mobile Unit Tests', 'Integration Tests', 'E2E Tests'],
    datasets: [{
      data: [82, 74, 68, 45],
      backgroundColor: ['#6366f1', '#f82581', '#10b981', '#f59e0b'],
      borderWidth: 0,
      cutout: '68%',
      spacing: 3,
    }],
  };

  const moduleCoverage = [
    { module: 'Auth', coverage: 92, color: '#10b981' },
    { module: 'Payments', coverage: 88, color: '#f59e0b' },
    { module: 'Streaming', coverage: 85, color: '#6366f1' },
    { module: 'Content API', coverage: 80, color: '#8b5cf6' },
    { module: 'User Management', coverage: 76, color: '#f82581' },
    { module: 'Analytics', coverage: 72, color: '#ef4444' },
    { module: 'Notifications', coverage: 65, color: '#14b8a6' },
  ];

  const bugResolutionData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Avg Days to Resolve',
      data: [4.2, 3.9, 3.5, 3.1, 2.7, 2.3],
      borderColor: '#10b981',
      backgroundColor: 'rgba(16,185,129,0.1)',
      tension: 0.4,
      fill: true,
      pointRadius: 4,
      pointBackgroundColor: '#10b981',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      borderWidth: 2.5,
    }],
  };

  const bugSeverityData = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [{
      label: 'Bugs',
      data: [8, 24, 52, 58],
      backgroundColor: ['#ef4444', '#f59e0b', '#6366f1', '#10b981'],
      borderRadius: 6,
      borderSkipped: false,
      barPercentage: 0.7,
    }],
  };

  const chartOpts = (yCallback) => ({
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: tickStyle, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: yCallback }, grid: gridStyle, border: noBorder },
    },
  });

  return (
    <div className="cd-section">
      <div className="cd-metrics-grid">
        {kpiMetrics.map((m, i) => (
          <div key={i} className="cd-metric-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="cd-metric-top">
              <div className="cd-metric-icon" style={{ background: m.color + '15', color: m.color }}><i className={m.icon}></i></div>
              <span className={`cd-metric-change ${m.positive ? 'up' : 'down'}`}><i className={`fas fa-arrow-${m.positive ? 'up' : 'down'}`}></i> {m.change}</span>
            </div>
            <div className="cd-metric-value">{m.value}</div>
            <div className="cd-metric-label">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="cd-row-2col">
        <div className="cd-chart-card">
          <div className="cd-chart-head">
            <h6>Test Coverage Breakdown</h6>
          </div>
          <div className="cd-chart-body cd-doughnut-wrap" style={{ height: 200 }}>
            <Doughnut data={testCoverageByType} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { ...tooltipStyle, callbacks: { label: ctx => `${ctx.label}: ${ctx.parsed}%` } } } }} />
          </div>
          <div className="cd-coverage-labels">
            {testCoverageByType.labels.map((label, i) => (
              <span key={i}><span className="cd-dot" style={{ background: testCoverageByType.datasets[0].backgroundColor[i] }}></span> {label} {testCoverageByType.datasets[0].data[i]}%</span>
            ))}
          </div>
        </div>
        <div className="cd-chart-card">
          <div className="cd-chart-head">
            <h6>Coverage by Module</h6>
          </div>
          <div className="cd-module-list">
            {moduleCoverage.map((m, i) => (
              <div key={i} className="cd-module-item">
                <span className="cd-module-name">{m.module}</span>
                <div className="cd-module-bar-wrap">
                  <div className="cd-module-bar" style={{ width: `${m.coverage}%`, background: m.color }}></div>
                </div>
                <span className="cd-module-pct">{m.coverage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="cd-row-2col">
        <div className="cd-chart-card">
          <div className="cd-chart-head">
            <h6>Bug Severity Distribution</h6>
          </div>
          <div className="cd-chart-body" style={{ height: 220 }}>
            <Bar data={bugSeverityData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false }, tooltip: tooltipStyle },
              scales: {
                x: { ticks: tickStyle, grid: noGrid, border: noBorder },
                y: { ticks: tickStyle, grid: gridStyle, border: noBorder },
              },
            }} />
          </div>
        </div>
        <div className="cd-chart-card">
          <div className="cd-chart-head">
            <h6>Bug Resolution Time Trend</h6>
          </div>
          <div className="cd-chart-body" style={{ height: 220 }}>
            <Line data={bugResolutionData} options={chartOpts(v => v.toFixed(1) + ' days')} />
          </div>
        </div>
      </div>
    </div>
  );
};

const TestingTab = () => {
  const sdkInventory = [
    { name: 'Firebase', version: '11.2.1', platform: 'Both', license: 'Commercial', lastUpdated: '2026-02-15', vulnerabilities: 0, status: 'current' },
    { name: 'Stripe', version: '5.8.0', platform: 'Backend', license: 'Commercial', lastUpdated: '2026-02-10', vulnerabilities: 0, status: 'current' },
    { name: 'ExoPlayer', version: '2.19.1', platform: 'Android', license: 'Apache', lastUpdated: '2026-02-08', vulnerabilities: 1, status: 'update-available' },
    { name: 'AVPlayer', version: '1.4.2', platform: 'iOS', license: 'MIT', lastUpdated: '2026-02-12', vulnerabilities: 0, status: 'current' },
    { name: 'Sentry', version: '7.82.0', platform: 'Both', license: 'MIT', lastUpdated: '2026-02-14', vulnerabilities: 0, status: 'current' },
    { name: 'Amplitude', version: '8.17.4', platform: 'Both', license: 'Commercial', lastUpdated: '2026-02-09', vulnerabilities: 0, status: 'current' },
    { name: 'Facebook SDK', version: '14.1.1', platform: 'Both', license: 'Commercial', lastUpdated: '2026-02-11', vulnerabilities: 1, status: 'update-available' },
    { name: 'AppsFlyer', version: '6.10.3', platform: 'Both', license: 'Commercial', lastUpdated: '2026-02-13', vulnerabilities: 0, status: 'current' },
    { name: 'Cloudflare', version: '1.12.0', platform: 'Backend', license: 'Commercial', lastUpdated: '2026-02-07', vulnerabilities: 0, status: 'current' },
    { name: 'FFmpeg', version: '5.1.2', platform: 'Backend', license: 'Apache', lastUpdated: '2026-02-06', vulnerabilities: 2, status: 'security-alert' },
  ];

  return (
    <div className="cd-section">
      <div className="cd-chart-card">
        <div className="cd-chart-head">
          <h6><i className="fas fa-box"></i> Third-Party SDK Inventory</h6>
          <span className="cd-badge-sm">10 SDKs</span>
        </div>
        <div className="cd-table-wrap">
          <table className="cd-table">
            <thead>
              <tr>
                <th>SDK Name</th>
                <th>Version</th>
                <th>Platform</th>
                <th>License</th>
                <th>Last Updated</th>
                <th>Vulnerabilities</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sdkInventory.map((sdk, i) => (
                <tr key={i}>
                  <td className="cd-sdk-name">{sdk.name}</td>
                  <td><code>{sdk.version}</code></td>
                  <td><span className="cd-badge" style={{ background: sdk.platform === 'Backend' ? 'rgba(99,102,241,0.2)' : sdk.platform === 'iOS' ? 'rgba(248,37,129,0.2)' : sdk.platform === 'Android' ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)' }}>{sdk.platform}</span></td>
                  <td><span className="cd-license">{sdk.license}</span></td>
                  <td>{sdk.lastUpdated}</td>
                  <td><span className={`cd-vuln ${sdk.vulnerabilities > 0 ? 'has-vulns' : ''}`}>{sdk.vulnerabilities}</span></td>
                  <td>
                    <span className={`cd-status-badge cd-status-${sdk.status}`}>
                      {sdk.status === 'current' && '✓ Current'}
                      {sdk.status === 'update-available' && '⬆ Update Available'}
                      {sdk.status === 'security-alert' && '⚠ Security Alert'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const DeploymentsTab = () => {
  const deployments = [
    { id: 'DEP-2847', branch: 'main', message: 'Merge pull request #1234: Fix video streaming lag...', duration: '4m 32s', status: 'success', deployedBy: 'Sarah Chen', timestamp: '2 mins ago' },
    { id: 'DEP-2846', branch: 'main', message: 'Add payment retry logic for failed transactions...', duration: '5m 18s', status: 'success', deployedBy: 'John Dev', timestamp: '1 hour ago' },
    { id: 'DEP-2845', branch: 'feature/notifications', message: 'Update push notification service config...', duration: '3m 45s', status: 'running', deployedBy: 'Alex Kumar', timestamp: '2 hours ago' },
    { id: 'DEP-2844', branch: 'hotfix/auth-bug', message: 'Fix OAuth token refresh timeout issue...', duration: '4m 20s', status: 'success', deployedBy: 'Maria Garcia', timestamp: '4 hours ago' },
    { id: 'DEP-2843', branch: 'main', message: 'Upgrade database connection pool settings...', duration: '6m 10s', status: 'failed', deployedBy: 'Chris Wong', timestamp: '6 hours ago' },
    { id: 'DEP-2842', branch: 'feature/analytics', message: 'Implement new user engagement metrics...', duration: '5m 02s', status: 'success', deployedBy: 'Emma Davis', timestamp: '8 hours ago' },
    { id: 'DEP-2841', branch: 'main', message: 'Merge pull request #1220: Performance optimization...', duration: '4m 58s', status: 'success', deployedBy: 'James Li', timestamp: '10 hours ago' },
    { id: 'DEP-2840', branch: 'feature/caching', message: 'Add Redis caching layer for API responses...', duration: '5m 45s', status: 'success', deployedBy: 'Nina Patel', timestamp: '12 hours ago' },
  ];

  const getStatusColor = (status) => {
    if (status === 'success') return '#10b981';
    if (status === 'failed') return '#ef4444';
    if (status === 'running') return '#f59e0b';
    return '#6366f1';
  };

  return (
    <div className="cd-section">
      <div className="cd-chart-card">
        <div className="cd-chart-head">
          <h6><i className="fas fa-code-branch"></i> CI/CD Pipeline Status</h6>
          <span className="cd-badge-sm">8 Recent Deployments</span>
        </div>
        <div className="cd-table-wrap">
          <table className="cd-table">
            <thead>
              <tr>
                <th>Pipeline ID</th>
                <th>Branch</th>
                <th>Commit Message</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Deployed By</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {deployments.map((dep, i) => (
                <tr key={i}>
                  <td className="cd-id">{dep.id}</td>
                  <td><code className="cd-branch">{dep.branch}</code></td>
                  <td className="cd-message" title={dep.message}>{dep.message}</td>
                  <td>{dep.duration}</td>
                  <td>
                    <span className={`cd-status-badge cd-status-${dep.status}`} style={{ borderColor: getStatusColor(dep.status) }}>
                      <i className={`fas fa-${dep.status === 'success' ? 'check-circle' : dep.status === 'failed' ? 'times-circle' : 'spinner fa-spin'}`}></i>
                      {dep.status.charAt(0).toUpperCase() + dep.status.slice(1)}
                    </span>
                  </td>
                  <td className="cd-deployer">{dep.deployedBy}</td>
                  <td className="cd-time">{dep.timestamp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const BugsTab = () => {
  const technicalDebt = [
    { title: 'Migrate to microservices architecture', category: 'Architecture', priority: 'high', effort: '8 weeks', status: 'planned' },
    { title: 'Update legacy authentication flow', category: 'Security', priority: 'high', effort: '3 weeks', status: 'in-progress' },
    { title: 'Optimize database query performance', category: 'Performance', priority: 'medium', effort: '2 weeks', status: 'in-progress' },
    { title: 'Refactor payment module code quality', category: 'Code Quality', priority: 'medium', effort: '4 weeks', status: 'backlog' },
    { title: 'Implement comprehensive logging system', category: 'Architecture', priority: 'low', effort: '1 week', status: 'backlog' },
  ];

  const knowledgeTransfer = [
    { label: 'Documentation Coverage', value: 72, icon: 'fas fa-book', color: '#6366f1' },
    { label: 'Onboarding Time', value: 21, max: 'days', icon: 'fas fa-clock', color: '#10b981', isTime: true },
    { label: 'Key Engineers Available', value: 4, max: 6, icon: 'fas fa-users', color: '#f59e0b' },
    { label: 'Runbooks Created', value: 18, max: 25, icon: 'fas fa-scroll', color: '#f82581' },
  ];

  const getPriorityColor = (priority) => {
    if (priority === 'high') return '#ef4444';
    if (priority === 'medium') return '#f59e0b';
    return '#10b981';
  };

  const getStatusColor = (status) => {
    if (status === 'in-progress') return '#6366f1';
    if (status === 'planned') return '#f59e0b';
    return '#8b5cf6';
  };

  return (
    <div className="cd-section">
      <div className="cd-row-2col">
        <div className="cd-chart-card">
          <div className="cd-chart-head">
            <h6><i className="fas fa-tools"></i> Technical Debt Tracker</h6>
          </div>
          <div className="cd-debt-list">
            {technicalDebt.map((item, i) => (
              <div key={i} className="cd-debt-item">
                <div className="cd-debt-header">
                  <h6>{item.title}</h6>
                  <span className="cd-badge" style={{ background: getPriorityColor(item.priority) + '25', color: getPriorityColor(item.priority) }}>
                    {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                  </span>
                </div>
                <div className="cd-debt-info">
                  <span className="cd-category">{item.category}</span>
                  <span className="cd-effort">{item.effort}</span>
                  <span className="cd-status" style={{ background: getStatusColor(item.status) + '25', color: getStatusColor(item.status) }}>
                    {item.status.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="cd-chart-card">
          <div className="cd-chart-head">
            <h6><i className="fas fa-graduation-cap"></i> Knowledge Transfer Readiness</h6>
          </div>
          <div className="cd-knowledge-grid">
            {knowledgeTransfer.map((item, i) => (
              <div key={i} className="cd-knowledge-card">
                <div className="cd-knowledge-icon" style={{ background: item.color + '15', color: item.color }}>
                  <i className={item.icon}></i>
                </div>
                <div className="cd-knowledge-content">
                  <div className="cd-knowledge-value">
                    {item.isTime ? `${item.value} ${item.max}` : item.max ? `${item.value}/${item.max}` : `${item.value}%`}
                  </div>
                  <div className="cd-knowledge-label">{item.label}</div>
                  {item.max && !item.isTime && (
                    <div className="cd-knowledge-bar">
                      <div className="cd-knowledge-bar-fill" style={{ width: `${(item.value / item.max) * 100}%`, background: item.color }}></div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodebaseDevOps;
